import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { me as getMe } from '../../api/auth'
import { practiceSignup } from '../../api/practice'
import PasswordField from '../../components/PasswordField'
import AuthPage, { AuthField } from '../../components/PublicLayout'
import { rememberCompanyId } from '../../utils/companyId'

// A free practice account for an accounting firm: its staff open the books
// of the companies that invite them. From an invite (?invite=), the invite
// is accepted as soon as the practice exists.
export default function PracticeSignup({ onSignedIn }) {
  const nav = useNavigate()
  const [params] = useSearchParams()
  const invite = params.get('invite') || ''
  const [firm, setFirm] = useState('')
  const [fullName, setFullName] = useState('')
  const [username, setUsername] = useState('')
  const [email, setEmail] = useState(params.get('email') || '')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [fields, setFields] = useState({})
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  const submit = async (e) => {
    e && e.preventDefault()
    if (!firm || !fullName || !username || !email || !password) { setErr('Fill in every field to create your practice.'); return }
    if (password !== confirm) { setErr('The two passwords don’t match.'); return }
    setBusy(true); setErr(''); setFields({})
    try {
      const u = await practiceSignup({ firm_name: firm, full_name: fullName, username, email: email.trim().toLowerCase(), password, invite: invite || undefined })
      rememberCompanyId(u?.tenant_id)
      onSignedIn && onSignedIn(await getMe())
      nav('/practice', { replace: true, state: { welcome: u?.tenant_id, accepted: u?.accepted } })
    } catch (e) {
      setFields(e?.fields || {})
      setErr(e?.message || 'Couldn’t create your practice.')
    } finally { setBusy(false) }
  }

  return (
    <AuthPage
      title="Create your practice"
      intro="Free for accountants and bookkeepers. Your clients invite you from their ASYS, and you open all their books from one place."
      top={<Link className="pub-btn pub-btn-ghost" to="/login">Sign in</Link>}
      foot={<><Link to="/accountants">What you get with a practice</Link><br />Running a business, not a practice? <Link to="/signup">Create your company</Link></>}
    >
      <form onSubmit={submit} className="pub-form" noValidate>
        {invite && <p className="pub-notice">Once your practice is ready, the invite you followed is accepted and the company is on your Clients list.</p>}
        <p className="pub-group">Your practice</p>
        <AuthField id="ps-firm" label="Practice name" hint="Your clients see it next to your name." error={fields.firm_name}>
          <input id="ps-firm" value={firm} onChange={(e) => setFirm(e.target.value)} autoComplete="organization" autoFocus />
        </AuthField>
        <p className="pub-group">You</p>
        <AuthField id="ps-name" label="Full name" error={fields.full_name}>
          <input id="ps-name" value={fullName} onChange={(e) => setFullName(e.target.value)} autoComplete="name" />
        </AuthField>
        <AuthField id="ps-email" label="Email" hint={invite ? 'The email the invite was sent to.' : undefined} error={fields.email}>
          <input id="ps-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
        </AuthField>
        <AuthField id="ps-username" label="Username" error={fields.username}>
          <input id="ps-username" value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" />
        </AuthField>
        <AuthField id="ps-password" label="Password" hint="At least 8 characters." error={fields.password}>
          <PasswordField id="ps-password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
        </AuthField>
        <AuthField id="ps-confirm" label="Password again">
          <PasswordField id="ps-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
        </AuthField>
        {err && <p className="pub-error" role="alert">{err}</p>}
        <button type="submit" className="pub-btn pub-btn-primary pub-btn-block" disabled={busy}>{busy ? 'Creating…' : 'Create my practice'}</button>
      </form>
    </AuthPage>
  )
}
